import {
    useQuery,
} from "@tanstack/react-query";
import {
    AlertCircle,
    RefreshCw,
} from "lucide-preact";
import {
    splClient,
} from "../../api/client.ts";

type TagStatsPanelProps = {
    onTagClick?: (tag: string) => void;
};

export const TagStatsPanel = ({
    onTagClick,
}: TagStatsPanelProps) => {
    const {
        data,
        isLoading,
        error,
        refetch,
    } = useQuery({
        queryKey: ["tagStats"],
        queryFn: () => splClient.getTagStats(),
        staleTime: 5 * 60 * 1000, // 5 minutes
        refetchOnWindowFocus: false,
    });

    if (isLoading) {
        return (
            <div className="space-y-3">
                <div className="h-4 bg-bg-tertiary rounded animate-pulse" />
                <div className="h-4 bg-bg-tertiary rounded animate-pulse w-3/4" />
                <div className="h-4 bg-bg-tertiary rounded animate-pulse w-1/2" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="text-sm text-error flex items-center gap-2">
                <AlertCircle size={14} />
                Failed to load stats
            </div>
        );
    }

    if (!data) return null;

    const {
        tags,
        total_items,
        max_count,
    } = data;

    // tag cloud font size scales with usage count
    const getFontSize = (count: number) => {
        const ratio = count / max_count;
        return 0.75 + ratio * 0.5;
    };

    const handleTagClick = (tagName: string, event: MouseEvent) => {
        if (event.altKey) {
            // alt+click adds as exclusion (NOT tag)
            onTagClick?.(`NOT ${tagName}`);
        } else {
            onTagClick?.(tagName);
        }
    };

    const renderTag = (
        tag: {
            tag: string;
            count: number;
            percentage: number;
        },
    ) => {
        const fontSize = getFontSize(tag.count);
        return (
            <button
                type="button"
                key={tag.tag}
                onClick={(e) => handleTagClick(tag.tag, e)}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-bg-tertiary hover:bg-border transition-colors group"
                style={{
                    fontSize: `${fontSize}rem`,
                }}
                title={`${tag.count} items (${tag.percentage}%) - Alt+Click to exclude`}
            >
                <span className="text-text-primary">
                    {tag.tag}
                </span>
                <span className="text-text-secondary text-xs opacity-75 group-hover:opacity-100">
                    {tag.count}
                </span>
            </button>
        );
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between text-xs text-text-secondary">
                <span>{total_items} total items</span>
                <button
                    type="button"
                    onClick={() => refetch()}
                    className="p-1 hover:text-text-primary transition-colors"
                    title="Refresh stats"
                >
                    <RefreshCw size={12} />
                </button>
            </div>

            {tags.length > 0 && (
                <div className="space-y-2">
                    <h4 className="text-xs text-text-secondary uppercase tracking-wider">
                        Tags ({tags.length})
                    </h4>
                    <div className="flex flex-wrap gap-1.5">
                        {tags.map(renderTag)}
                    </div>
                </div>
            )}
        </div>
    );
};
